import { MAIN_MENU_MUSIC_PATH } from '../config/mainMenuMusic';
import {
  BOOT_LOGO_PATH,
  WELCOME_VIDEO_POSTER_PATH,
} from '../config/welcomeAssets';
import { preloadTracks } from '../hooks/musicCache';
import {
  BOOT_PRELOAD_SOUNDS,
  preloadImage,
  preloadSoundEffectPaths,
} from '../hooks/mediaPreload';

/** Ordered web boot stages — welcome video is deferred until Welcome mounts. */
const WEB_BOOT_STAGES = [
  {
    key: 'boot.logo',
    load: () => preloadImage(BOOT_LOGO_PATH),
  },
  {
    key: 'boot.uiSounds',
    load: async () => {
      const results = await preloadSoundEffectPaths(BOOT_PRELOAD_SOUNDS);
      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `UI sound preload failed: ${failed.length}/${results.length} rejected`
        );
      }
    },
  },
  {
    key: 'boot.menuMusic',
    load: async () => {
      const results = await preloadTracks([MAIN_MENU_MUSIC_PATH]);
      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(
          `Menu music preload failed: ${failed.length}/${results.length} rejected`
        );
      }
    },
  },
  {
    key: 'boot.welcomePoster',
    load: () => preloadImage(WELCOME_VIDEO_POSTER_PATH),
    optional: true,
  },
];

/**
 * Preload critical web assets before revealing the app shell.
 * @param {{ onProgress?: (payload: { fraction: number, stageKey: string }) => void }} options
 * @returns {Promise<void>}
 */
export async function runWebBootLoad({ onProgress } = {}) {
  if (!Array.isArray(WEB_BOOT_STAGES) || WEB_BOOT_STAGES.length === 0) {
    throw new Error('runWebBootLoad: WEB_BOOT_STAGES must be a non-empty array');
  }

  const total = WEB_BOOT_STAGES.length;
  let completed = 0;

  const report = (stageKey, fraction) => {
    if (typeof onProgress !== 'function') {
      return;
    }
    onProgress({ fraction, stageKey });
  };

  report('boot.starting', 0);

  for (const stage of WEB_BOOT_STAGES) {
    report(stage.key, completed / total);
    try {
      await stage.load();
    } catch (err) {
      if (stage.optional === true) {
        console.warn(`Web boot optional stage failed (${stage.key}):`, err);
      } else {
        console.error(`Web boot stage failed (${stage.key}):`, err);
      }
    }
    completed += 1;
    report(stage.key, completed / total);
  }

  report('boot.ready', 1);
}

export { WEB_BOOT_STAGES };
